// Browser detection utility for identifying problematic browsers
// and checking feature compatibility

export const browserDetection = {
  // Check if user is on DuckDuckGo browser
  isDuckDuckGo() {
    const ua = navigator.userAgent || '';
    // The real desktop app's UA doesn't contain the literal word "DuckDuckGo" -
    // it appends a "Ddg/<version>" token instead (confirmed against the actual
    // macOS app: "...Safari/605.1.15 Ddg/26.5"), deliberately blending in with
    // a standard WebKit/Safari UA to minimize fingerprinting. Check for both
    // in case other platforms/versions format it differently.
    return ua.includes('DuckDuckGo') || /\bDdg\/\d/.test(ua);
  },

  // Check if user is on any known problematic browser
  isProblematicBrowser() {
    return this.isDuckDuckGo() || this.isOperaMini() || this.isIE();
  },

  // Check for Opera Mini (also breaks WebSockets)
  isOperaMini() {
    const ua = navigator.userAgent || '';
    return ua.includes('Opera Mini');
  },

  // Check for Internet Explorer
  isIE() {
    const ua = window.navigator.userAgent;
    return ua.indexOf('MSIE ') > 0 || ua.indexOf('Trident/') > 0;
  },

  // Get browser name for display
  getBrowserName() {
    const ua = navigator.userAgent;
    if (this.isDuckDuckGo()) return 'DuckDuckGo';
    if (this.isOperaMini()) return 'Opera Mini';
    if (this.isIE()) return 'Internet Explorer';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown Browser';
  },

  // Check WebSocket support
  hasWebSocketSupport() {
    return 'WebSocket' in window && window.WebSocket !== undefined;
  },

  // Check localStorage support
  hasLocalStorageSupport() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  },

  // Check all required features
  checkCompatibility() {
    return {
      browser: this.getBrowserName(),
      isDuckDuckGo: this.isDuckDuckGo(),
      isProblematic: this.isProblematicBrowser(),
      hasWebSockets: this.hasWebSocketSupport(),
      hasLocalStorage: this.hasLocalStorageSupport(),
      issues: this.getIssues(),
    };
  },

  // Get specific issues with current browser
  getIssues() {
    const issues = [];

    if (this.isDuckDuckGo()) {
      issues.push({
        severity: 'critical',
        message:
          'DuckDuckGo browser blocks WebSocket connections required for multiplayer gameplay',
        detail: 'Privacy protection features prevent real-time game synchronization',
      });
    }

    if (!this.hasWebSocketSupport()) {
      issues.push({
        severity: 'critical',
        message: 'Your browser does not support WebSockets',
        detail: 'Real-time multiplayer requires WebSocket support',
      });
    }

    if (!this.hasLocalStorageSupport()) {
      issues.push({
        severity: 'warning',
        message: 'localStorage is not available',
        detail: 'Game settings and username may not persist',
      });
    }

    if (this.isOperaMini()) {
      issues.push({
        severity: 'critical',
        message: 'Opera Mini does not support real-time connections',
        detail: 'This browser cannot handle WebSocket connections',
      });
    }

    if (this.isIE()) {
      issues.push({
        severity: 'critical',
        message: 'Internet Explorer is not supported',
        detail: 'Please use a modern browser for the best experience',
      });
    }

    return issues;
  },
};

export default browserDetection;
